import React from "react";
import useDimensions from "../../hooks/useDimensions";
import styles from "./pillar.module.scss";
import PILLAR_DATA from "../../data/pillars.json";
import { generatePhyllotaxis } from "./phyllotaxis";
import { range } from "d3-array";
import { IllustrationForward, IllustrationMoment, IllustrationRetro } from "./pillar-illustrations";
import Carousel from "nuka-carousel";
import useScreenVisible from "../../hooks/useScreenVisible";
import gtmPush from "../../modules/gtm";

const PILLARS = [
    {
        name: "Financial forward thinkers",
        key: "forward",
        illustration: IllustrationForward,
        cards: [
            {
                label: "have a <em>single super account</em> (and know how much is in it)",
            },
            {
                label: "have a <em>realistic budget</em> and <em>savings goals</em>",
            },
            {
                label: "always <em>pay bills</em> and <em>credit card repayments on time</em>",
            },
        ],
    },
    {
        name: "In-the-moment thinkers",
        key: "moment",
        illustration: IllustrationMoment,
        cards: [
            {
                label:
                    "mostly <em>stick to a budget</em>, but <em>don't use financial tools</em> to manage it",
            },
            {
                label: "<em>don't track irregular expenses</em> (like Friday night take-away)",
            },
            {
                label: "have <em>debt</em>, but always <em>pay it off in full</em>",
            },
        ],
    },
    {
        name: "Retrospective thinkers",
        key: "retro",
        illustration: IllustrationRetro,
        cards: [
            {
                label:
                    "<em>struggle</em> to stay on top of <em>bills</em>, and <em>savings goals</em>",
            },
            {
                label: "are <em>dealing with debt</em>, but <em>don't track their spending</em>",
            },
            {
                label:
                    "sometimes <em>dip into their savings</em> to <em>pay</em> their <em>bills</em>",
            },
        ],
    },
];

const Pillar = props => {
    const { pillarKey } = props;

    const headerRef = React.useRef(null);
    const headerDimensions = useDimensions(headerRef);

    const pillar = React.useMemo(() => {
        return PILLARS.find(d => d.key === pillarKey);
    }, [pillarKey]);

    const [openCards, setOpenCards] = React.useState([1]);

    const markCardOpened = React.useCallback(
        index => {
            if (openCards.includes(index)) return;
            gtmPush({
                event: "pillar-slide",
                slide: index,
                pillar: pillarKey,
            });
            setOpenCards([...openCards, index]);
        },
        [openCards, pillarKey]
    );

    // We fade in 0 when we first load. It's always the one in view when we first see it.
    // const visibleRef = useScreenVisible(() => markCardOpened(1), 128);

    if (!pillar) return null;

    const cards = pillar.cards.map((card, cardIndex) => (
        <PillarCard
            key={card.label}
            pillarKey={pillarKey}
            index={cardIndex}
            {...card}
            illustration={pillar.illustration}
            opened={openCards.includes(cardIndex)}
        />
    ));
    return (
        <div className={styles.pillar}>
            <div className={styles.pillarHeading} ref={headerRef}>
                {pillar.name}
            </div>
            <div className={styles.pillarCardContainer}>
                {headerDimensions && (
                    <Carousel
                        scrollMode="remainder"
                        slidesToShow={3}
                        slidesToScroll={1}
                        cellAlign="center"
                        transitionMode="scroll"
                        slideWidth={headerDimensions.width + 32 + "px"}
                        slideIndex={1}
                        renderCenterLeftControls={({ previousSlide }) => (
                            <div
                                className={styles.pillarCarouselOverlay}
                                data-side="left"
                                onClick={e => {
                                    previousSlide();
                                }}
                            />
                        )}
                        renderCenterRightControls={({ nextSlide }) => (
                            <div
                                className={styles.pillarCarouselOverlay}
                                data-side="right"
                                onClick={nextSlide}
                            />
                        )}
                        disableEdgeSwiping
                        defaultControlsConfig={{
                            pagingDotsContainerClassName: styles.pageDots,
                            pagingDotsClassName: styles.pageDot,
                        }}
                        afterSlide={markCardOpened}
                    >
                        {cards}
                    </Carousel>
                )}
            </div>
        </div>
    );
};

const ANIMATION_DURATIONS = {
    forward: 1,
    moment: 1,
    retro: 1,
};

const PillarCard = props => {
    const { label, index, pillarKey, illustration, opened } = props;

    const ref = React.useRef(null);
    const visDimensions = useDimensions(ref);

    const value = Math.round(PILLAR_DATA[pillarKey][index]);

    const Illustration = illustration;

    return (
        <div className={styles.pillarCard}>
            <div
                className={styles.cardLabel}
                data-index={index}
                dangerouslySetInnerHTML={{ __html: label }}
            />
            <div className={styles.visArea} ref={ref}>
                {visDimensions && (
                    <PillarVis
                        opened={opened}
                        value={value}
                        dimensions={visDimensions}
                        {...props}
                    />
                )}
                <div className={styles.visIllustrationContainer}>
                    <Illustration />
                </div>
            </div>
            <div
                className={styles.valueArea}
                data-opened={opened}
                style={{
                    animationDuration: ANIMATION_DURATIONS[pillarKey] + "s",
                    animationDelay: ANIMATION_DURATIONS[pillarKey] * (value / 100) + "s",
                }}
            >
                <span className={styles.value} data-index={index}>
                    {value}%
                </span>
                <span className={styles.valueSub}>of all participants</span>
            </div>
        </div>
    );
};

const PillarVis = props => {
    const { value, dimensions, index, pillarKey, opened } = props;
    const phyllotaxis = React.useMemo(() => {
        const phyllo = generatePhyllotaxis(
            range(0, 100),
            { x: dimensions.width / 2, y: dimensions.height / 2 },
            {
                innerRadius: 70,
                outerRadius: 100,
                circleRadius: "auto",
                maxAutoSize: Number.POSITIVE_INFINITY,
                circlePackRatio: 0.1,
            }
        );

        return phyllo;
    }, [dimensions]);

    const maxDuration = ANIMATION_DURATIONS[pillarKey];
    // Make bigger values animate faster.
    const duration = maxDuration; //maxDuration / 2 + ((100 - value) / 100) * (maxDuration / 2);

    const dots = phyllotaxis.map(dot => {
        const selected = dot.index <= value;
        return (
            <PillarDot
                key={dot.index}
                index={dot.index}
                pillarKey={pillarKey}
                selected={selected}
                x={dot.x}
                y={dot.y}
                r={dot.r}
                animDuration={duration}
                opened={opened}
                cardIndex={index}
            />
        );
    });

    return (
        <svg className={styles.visSvg} data-index={index}>
            {dots}
        </svg>
    );
};

const PillarDot = props => {
    const { selected, x, y, r, index, pillarKey, animDuration, opened, cardIndex } = props;

    const pct = index / 100;
    const animDelay = pct * animDuration;

    return (
        <circle
            className={styles.dot}
            data-selected={selected}
            data-pillar={pillarKey}
            data-opened={opened}
            data-cardindex={cardIndex}
            cx={x}
            cy={y}
            r={r}
            style={{
                animationDelay: animDelay + "s",
                animationDuration: animDuration + "s",
                transformOrigin: `${x}px ${y}px`,
            }}
        />
    );
};

export default Pillar;
